import React from 'react';
import { BlurView } from 'expo-blur';
import type { TouchableOpacityProps } from 'react-native';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

import { pointerEventsStyles, BodyText, LoadingDots, theme } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationsMapSearchAreaButton = (props: Props) => {
  const { accessibilityLabel, children, disabled, isLoading, onPress } = props;

  const isDisabled = Boolean(disabled) || Boolean(isLoading);
  const textStyles = isDisabled ? styles.textDisabled : styles.text;

  return (
    <TouchableOpacity
      activeOpacity={ACTIVE_OPACITY}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ disabled: isDisabled, busy: Boolean(isLoading) }}
      aria-busy={isLoading}
      style={styles.button}
      disabled={isDisabled}
      onPress={onPress}
    >
      <View style={[styles.background, pointerEventsStyles.none]}>
        <BlurView intensity={BLUR_INTENSITY} tint="light" style={styles.blur} />
      </View>

      {isLoading ? (
        <LoadingDots color={theme.colors.TEXT_COLOR} />
      ) : (
        <BodyText sizeMatch={['14']} style={[textStyles, pointerEventsStyles.none]}>
          {children}
        </BodyText>
      )}
    </TouchableOpacity>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const ACTIVE_OPACITY = 0.8;
const BLUR_INTENSITY = 85;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  button: {
    height: 40,
    minWidth: 122,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing['6'],
    borderRadius: theme.radius['3xLarge'],
    ...theme.elevations['4'],
  },
  background: {
    ...StyleSheet.absoluteFill,
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: 'rgba(250, 250, 246, 0.7)',
    overflow: 'hidden',
  },
  blur: {
    ...StyleSheet.absoluteFill,
    borderRadius: theme.radius['3xLarge'],
    overflow: 'hidden',
  },
  text: {
    color: theme.colors.TEXT_COLOR,
  },
  textDisabled: {
    color: theme.colors.OPACITY.DARK_KALE.LIGHT,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  accessibilityLabel: string;
  children: string;
  disabled?: boolean;
  isLoading?: boolean;
  onPress?: TouchableOpacityProps['onPress'];
};
